// Hideo - state of the Keywords page that outlives it: the search (typed text, suggestions found, loading and error),
// the Google Trends comparison (keywords and options: src/features/trends.ts)
// and the bank filter and confirmation message, kept while moving between pages (like before). The second tab of the
// video dialog starts a search from here before the page is drawn ("Find ideas": searchFor, SeoPanel.vue).
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { t } from '../i18n'
import { kwNorm, searchSuggestions, type KwFilter } from '../features/keywords'
import { TRENDS_MAX, trendsAdd, trendsDefaults, type TrendsOpts } from '../features/trends'

export const useKeywordsStore = defineStore('keywords', () => {
  const query = ref('')
  const results = ref<string[]>([])
  const loading = ref(false)
  const err = ref('')
  const filter = ref<KwFilter>('all')
  const msg = ref('')

  /** Searches the typed text (more: also with the letters A–Z) */
  async function search(more = false): Promise<void> {
    const q = kwNorm(query.value)
    if (!q || loading.value) return
    loading.value = true
    err.value = ''
    const r = await searchSuggestions(q, more)
    loading.value = false
    results.value = r.results
    err.value = r.ok ? (r.results.length ? '' : t('No suggestions: try simpler words.')) : t("I can't reach YouTube: are you connected to the internet?")
  }
  /** Puts a text in the search box and searches it (if not empty) */
  function searchFor(q: string): void {
    query.value = q
    if (q.trim()) search()
  }

  // ===== Google Trends =====
  const trends = ref<string[]>([])
  const trendsOpts = ref<TrendsOpts>(trendsDefaults())
  const inTrends = (text: string): boolean => trends.value.includes(kwNorm(text))
  /** Adds a keyword to the comparison: false when the list is already full */
  function addTrend(text: string): boolean {
    if (inTrends(text)) return true
    if (trends.value.length >= TRENDS_MAX) return false
    trends.value = trendsAdd(trends.value, text)
    return true
  }
  function removeTrend(text: string): void {
    const k = kwNorm(text)
    trends.value = trends.value.filter(x => x !== k)
  }
  /** In the comparison or out of it (the chart button of the bank): false when it could not go in (list full) */
  function toggleTrend(text: string): boolean {
    if (inTrends(text)) { removeTrend(text); return true }
    return addTrend(text)
  }

  let msgTimer: ReturnType<typeof setTimeout> | undefined
  /** Short green confirmation above the bank */
  function flash(m: string): void {
    msg.value = m
    clearTimeout(msgTimer)
    msgTimer = setTimeout(() => { msg.value = '' }, 3500)
  }

  return { query, results, loading, err, filter, msg, search, searchFor, flash,
    trends, trendsOpts, inTrends, addTrend, removeTrend, toggleTrend }
})
